import React, { useState, useRef } from 'react';
import { 
  Card, 
  Text, 
  Button, 
  Group, 
  Stack, 
  Badge,
  Paper,
  Box
} from '@mantine/core';
import { 
  Sparkles,
  ArrowRight,
  Star,
  Zap,
  Target
} from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter
} from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './FeaturePreviewDemo.css';

interface Task {
  id: string;
  title: string;
  color: string;
  emoji: string;
}

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
  emoji: string;
}

// Confetti hook for magical effects
const useConfetti = () => {
  const [confetti, setConfetti] = useState<ConfettiPiece[]>([]);
  const confettiRef = useRef<number>(0);

  const triggerConfetti = (x: number = 50, y: number = 50) => {
    const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA726', '#AB47BC'];
    const emojis = ['✨', '🎉', '⭐', '💫', '🌟', '🎊'];
    
    const newConfetti: ConfettiPiece[] = [];
    for (let i = 0; i < 15; i++) {
      newConfetti.push({
        id: confettiRef.current++,
        x: x + (Math.random() - 0.5) * 40,
        y: y + (Math.random() - 0.5) * 40,
        rotation: Math.random() * 360,
        color: colors[Math.floor(Math.random() * colors.length)],
        emoji: emojis[Math.floor(Math.random() * emojis.length)]
      });
    }
    
    setConfetti(prev => [...prev, ...newConfetti]);
    
    // Clean up confetti after animation
    setTimeout(() => {
      setConfetti(prev => prev.filter(c => !newConfetti.includes(c)));
    }, 2000);
  };

  return { confetti, triggerConfetti };
};

// Sortable task card for Kanban
const SortableTask: React.FC<{ 
  task: Task;
  containerId: string; // column id
}> = ({ task, containerId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: task.id, data: { type: 'task', task, containerId } });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
    cursor: 'grab',
  };

  return (
    <Card 
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      p="sm"
      withBorder
      className="demo-task"
      shadow="sm"
    >
      <Group gap="xs" align="center">
        <span style={{ fontSize: '16px' }}>{task.emoji}</span>
        <Text size="sm" fw={500} style={{ flex: 1 }}>
          {task.title}
        </Text>
        <Sparkles size={14} style={{ opacity: 0.6 }} />
      </Group>
    </Card>
  );
};

// Droppable column for Kanban
const DroppableColumn: React.FC<{
  id: string;
  title: string;
  color: string;
  children: React.ReactNode;
  isOver?: boolean;
}> = ({ id, title, color, children, isOver }) => {
  const { setNodeRef } = useDroppable({ id, data: { type: 'column', containerId: id } });

  return (
    <Paper
      id={id}
      ref={setNodeRef}
      p="md"
      withBorder
      className={`demo-column ${isOver ? 'demo-column-over' : ''}`}
      style={{
        minHeight: '300px',
        backgroundColor: isOver ? 'var(--mantine-color-blue-0)' : 'var(--app-surface)',
        borderColor: color,
        borderWidth: '2px',
        transition: 'all 0.3s ease',
        transform: isOver ? 'scale(1.02)' : 'scale(1)',
      }}
    >
      <Group gap="xs" mb="sm">
        <Box
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
        <Text fw={600} size="sm" style={{ color: 'var(--app-text-primary)' }}>
          {title}
        </Text>
        <Sparkles size={12} style={{ opacity: 0.5 }} />
      </Group>
      <Stack gap="xs">
        {children}
      </Stack>
    </Paper>
  );
};

export const KanbanPreviewDemo: React.FC<{
  onUpgradeClick?: () => void;
}> = ({ onUpgradeClick }) => {
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [moveCount, setMoveCount] = useState(0);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const { confetti, triggerConfetti } = useConfetti();

  const [kanbanTasks, setKanbanTasks] = useState({
    todo: [
      { id: 'k1', title: 'Plan vacation', color: '#FFB800', emoji: '🏖️' },
      { id: 'k2', title: 'Buy groceries', color: '#FF6B6B', emoji: '🛒' },
    ],
    doing: [
      { id: 'k3', title: 'Learn React', color: '#4ECDC4', emoji: '⚛️' },
    ],
    done: [
      { id: 'k4', title: 'Morning workout', color: '#45B7D1', emoji: '💪' },
    ],
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
  const data = event.active.data.current as any;
  setDraggedTask((data?.task as Task) || (data as Task) || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedTask(null);

  if (!over || !active) return;

  const activeData = active.data.current as any;
  const task: Task | undefined = activeData?.task || activeData;
  if (!task) return;
  const targetId = String(over.id);

    // Increment move counter
    setMoveCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 3) {
        setShowUpgradePrompt(true);
        setTimeout(() => setShowUpgradePrompt(false), 4000);
        
        // Auto-scroll to show upgrade button on short screens
        setTimeout(() => {
          const upgradePrompt = document.querySelector('.upgrade-prompt');
          if (upgradePrompt) {
            upgradePrompt.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 100); // Small delay to ensure DOM is updated
      }
      return newCount;
    });

    // Trigger confetti at drop location
    const rect = document.getElementById(targetId as string)?.getBoundingClientRect();
    if (rect) {
      const x = ((rect.left + rect.width / 2) / window.innerWidth) * 100;
      const y = ((rect.top + rect.height / 2) / window.innerHeight) * 100;
      triggerConfetti(x, y);
    } else {
      triggerConfetti();
    }

    // Determine source container
    const sourceColumn = (activeData?.containerId as string) || Object.keys(kanbanTasks).find(col =>
      (kanbanTasks as any)[col].some((t: Task) => t.id === task.id)
    );
    if (!sourceColumn) return;

    // Determine target container and index
    const overData = over.data.current as any;
    let targetColumn: string | undefined;
    let targetIndex: number | undefined;

    if (overData?.type === 'task') {
      targetColumn = overData.containerId as string | undefined;
      const list: Task[] = targetColumn ? (kanbanTasks as any)[targetColumn] || [] : [];
      const overIdx = list.findIndex((t: Task) => t.id === over.id);
      targetIndex = overIdx >= 0 ? overIdx : list.length;
    } else if (overData?.type === 'column' || ['todo','doing','done'].includes(targetId)) {
      targetColumn = (overData?.containerId as string | undefined) || targetId;
      const list: Task[] = targetColumn ? (kanbanTasks as any)[targetColumn] || [] : [];
      targetIndex = list.length;
    }

    if (!targetColumn) return;

    setKanbanTasks(prev => {
      const next: Record<string, Task[]> = { ...prev } as any;
      const fromList = [...(next[sourceColumn] || [])];
      const fromIndex = fromList.findIndex((t: Task) => t.id === task.id);
      if (fromIndex === -1) return prev;
      const [moved] = fromList.splice(fromIndex, 1);

      if (sourceColumn === targetColumn) {
        // Reorder within same column
        next[targetColumn] = arrayMove([...(next[targetColumn] || [])], fromIndex, Math.min(targetIndex!, (next[targetColumn] || []).length));
      } else {
        // Move across columns
        const toList = [...(next[targetColumn] || [])];
        const insertAt = Math.min(targetIndex ?? toList.length, toList.length);
        toList.splice(insertAt, 0, moved);
        next[sourceColumn] = fromList;
        next[targetColumn] = toList;
      }
      return next as any;
    });
  };

  return (
    <div className="feature-preview-demo">
      {/* Confetti overlay */}
      <div className="confetti-container">
        {confetti.map(piece => (
          <div
            key={piece.id}
            className="confetti-piece"
            style={{
              left: `${piece.x}%`,
              top: `${piece.y}%`,
              transform: `rotate(${piece.rotation}deg)`,
              color: piece.color,
            }}
          >
            {piece.emoji}
          </div>
        ))}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Card 
          withBorder 
          p="xl" 
          className="demo-card"
          style={{ maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden', paddingBottom: 32 }}
        >
          <Stack gap="lg">
            {/* Move counter */}
            <Group justify="flex-end">
              <Badge 
                variant="light" 
                color={moveCount >= 3 ? 'orange' : 'blue'}
                leftSection={<Target size={12} />}
              >
                {moveCount}/3 moves
              </Badge>
            </Group>

            {/* Kanban Demo */}
            <Group align="stretch" gap="md" wrap="nowrap" style={{ overflowX: 'auto' }}>
              <DroppableColumn id="todo" title="To Do" color="#FF6B6B">
                <SortableContext items={kanbanTasks.todo.map(t => t.id)}>
                  {kanbanTasks.todo.map(task => (
                    <SortableTask key={task.id} task={task} containerId="todo" />
                  ))}
                </SortableContext>
              </DroppableColumn>
              
              <DroppableColumn id="doing" title="Doing" color="#FFB800">
                <SortableContext items={kanbanTasks.doing.map(t => t.id)}>
                  {kanbanTasks.doing.map(task => (
                    <SortableTask key={task.id} task={task} containerId="doing" />
                  ))}
                </SortableContext>
              </DroppableColumn>
              
              <DroppableColumn id="done" title="Done" color="#45B7D1">
                <SortableContext items={kanbanTasks.done.map(t => t.id)}>
                  {kanbanTasks.done.map(task => (
                    <SortableTask key={task.id} task={task} containerId="done" />
                  ))}
                </SortableContext>
              </DroppableColumn>
            </Group>

            {/* Upgrade prompt */}
            {(moveCount >= 3 || showUpgradePrompt) && (
              <Paper 
                p="md" 
                withBorder 
                className="upgrade-prompt"
                style={{
                  background: 'linear-gradient(135deg, var(--app-surface) 0%, var(--app-hover) 100%)',
                  borderColor: 'var(--mantine-color-blue-4)',
                  borderWidth: '2px',
                  boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
                }}
              >
                <Group justify="space-between" align="center">
                  <Group gap="sm">
                    <Zap size={20} style={{ color: 'var(--mantine-color-orange-5)' }} />
                    <div>
                      <Text fw={600} size="sm" style={{ color: 'var(--app-text-primary)' }}>
                        🎉 You're a natural organizer!
                      </Text>
                      <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                        Upgrade for unlimited columns, advanced features, and team collaboration
                      </Text>
                    </div>
                  </Group>
                  
                  <Button 
                    size="sm"
                    variant="gradient"
                    gradient={{ from: 'blue', to: 'purple', deg: 45 }}
                    leftSection={<Star size={14} />}
                    rightSection={<ArrowRight size={14} />}
                    onClick={onUpgradeClick}
                  >
                    Upgrade Now
                  </Button>
                </Group>
              </Paper>
            )}

            {/* Feature highlights */}
            <Paper p="md" withBorder className="features-highlight">
              <Text fw={600} size="sm" mb="xs" style={{ color: 'var(--app-text-primary)' }}>
                ✨ Premium Kanban Features Include:
              </Text>
              <Group gap="lg">
                <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                  • Unlimited custom columns
                </Text>
                <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                  • Advanced card features
                </Text>
                <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                  • Team collaboration
                </Text>
                <Text size="xs" style={{ color: 'var(--app-text-secondary)' }}>
                  • Visual task organization
                </Text>
              </Group>
            </Paper>

            {/* Bottom spacer to ensure final content can scroll fully into view */}
            <div style={{ height: 48 }} />
          </Stack>
        </Card>

        {/* Drag overlay */}
        <DragOverlay>
          {draggedTask && (
            <div className="drag-overlay">
              <Card p="sm" withBorder className="demo-task" shadow="lg">
                <Group gap="xs" align="center">
                  <span style={{ fontSize: '16px' }}>{draggedTask.emoji}</span>
                  <Text size="sm" fw={500} style={{ flex: 1 }}>
                    {draggedTask.title}
                  </Text>
                  <Sparkles size={14} style={{ opacity: 0.6 }} />
                </Group>
              </Card>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};